import { ChatCitationsProvider } from "./ChatCitations";
import { ChatUserQuestion } from "./ChatUserQuestion";
import { isUserQuestionItem } from "./chat-user-question";
import { ChatToolItem } from "./ChatToolItem";
import { ChatAttachmentPreview } from "./ChatAttachmentPreview";
import { ChatResponseText } from "./ChatResponseText";
import {
  ChatApprovalCard,
  type ChatApprovalCardReview,
} from "@/components/features/ai-chat/ChatApprovalCard";
import { Button } from "@/components/ui";
import type {
  AiChatQuestionAnswer,
  AiChatQuestionResult,
  AiChatGuardianDecision,
  AiChatGuardianReview,
  AiChatGuardianReviewStatus,
  AiChatItemBodyPart,
  AiChatLoadedItemBody,
  AiChatTimelineItem,
} from "@/lib/types/ai-chat";
import { LoaderCircle } from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  memo,
  type ReactNode,
} from "react";

export {
  ChatApprovalCard,
  type ChatApprovalCardProps,
  type ChatApprovalCardReview,
} from "@/components/features/ai-chat/ChatApprovalCard";

export type ChatTimelineItemProps = {
  onAnswerQuestion?: (
    runId: string,
    requestId: string,
    answers: AiChatQuestionAnswer[],
  ) => Promise<AiChatQuestionResult>;
  footer?: ReactNode;
  item: AiChatTimelineItem;
  approvalDetail?: AiChatGuardianReview;
  body?: AiChatLoadedItemBody;
  isApprovalLoading?: boolean;
  isBodyLoading?: boolean;
  isDecidingApproval?: boolean;
  onDecideApproval?: (
    runId: string,
    reviewId: string,
    decision: AiChatGuardianDecision,
  ) => Promise<void> | void;
  onLoadApprovalDetail?: (
    runId: string,
    reviewId: string,
  ) => Promise<void> | void;
  onLoadItemBody?: (
    item: AiChatTimelineItem,
    part: AiChatItemBodyPart,
  ) => Promise<void> | void;
};

function prettyData(text: string) {
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
}

function AssistantItem({
  item,
  body: loadedBody,
  isBodyLoading,
  onLoadItemBody,
}: Pick<
  ChatTimelineItemProps,
  "item" | "body" | "isBodyLoading" | "onLoadItemBody"
>) {
  const [loadError, setLoadError] = useState(false);
  const body = loadedBody?.revision === item.revision ? loadedBody : undefined;
  const displayedText = body?.part === "text" ? body.text : item.text;
  const inlineTextIsTruncated = item.text?.endsWith("…") ?? false;
  const canLoadText =
    item.hasBody && inlineTextIsTruncated && body?.part !== "text";
  const reachedPreviewLimit = body?.reachedClientLimit ?? false;
  const hasMoreText =
    body?.part === "text" && !body.isComplete && !reachedPreviewLimit;
  const canLoadData =
    !Array.isArray(item.payload.fileCitations) &&
    ((body?.availableParts.includes("data") && body.part !== "data") ||
      (item.hasBody && !inlineTextIsTruncated && !body));
  const loadBody = async (part: AiChatItemBodyPart) => {
    if (!onLoadItemBody) return;
    setLoadError(false);
    try {
      await onLoadItemBody(item, part);
    } catch {
      setLoadError(true);
    }
  };

  return (
    <article className="text-foreground min-w-0">
      <div data-chat-search-content>
        {displayedText ? (
          <ChatCitationsProvider
            citations={item.payload.fileCitations}
            streaming={item.status === "streaming"}
          >
            <ChatResponseText
              text={displayedText}
              status={body?.part === "text" ? undefined : item.status}
            />
          </ChatCitationsProvider>
        ) : (
          <p className="text-muted-foreground text-sm">
            Response content is unavailable.
          </p>
        )}
      </div>
      {(canLoadText || hasMoreText || canLoadData) && onLoadItemBody ? (
        <div className="mt-3 flex flex-wrap gap-2">
          {(canLoadText || hasMoreText) && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-muted-foreground h-8 px-2 text-xs"
              disabled={isBodyLoading}
              onClick={() => {
                void loadBody("text");
              }}
            >
              {isBodyLoading && <LoaderCircle className="animate-spin" />}
              {hasMoreText ? "Load more" : "Show full response"}
            </Button>
          )}
          {canLoadData && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-muted-foreground h-8 px-2 text-xs"
              disabled={isBodyLoading}
              onClick={() => {
                void loadBody("data");
              }}
            >
              View response details
            </Button>
          )}
        </div>
      ) : null}
      {body?.part === "data" ? (
        <pre className="border-border/70 bg-muted/40 mt-3 max-h-80 overflow-auto rounded-xl border p-3 text-xs whitespace-pre-wrap">
          {prettyData(body.text)}
        </pre>
      ) : null}
      {reachedPreviewLimit ? (
        <p className="text-muted-foreground mt-2 text-xs">
          Preview limit reached
        </p>
      ) : null}
      {loadError ? (
        <p role="alert" className="text-destructive mt-2 text-xs">
          The response details could not be loaded. Try again.
        </p>
      ) : null}
    </article>
  );
}

const guardianStatuses: AiChatGuardianReviewStatus[] = [
  "pending_guardian",
  "awaiting_human",
  "approved",
  "rejected",
  "invalidated",
  "cancelled",
];

function ApprovalItem(props: ChatTimelineItemProps) {
  const { item } = props;
  if (!item.runId || !item.guardianReviewId) {
    return (
      <div className="border-border/70 bg-muted/20 text-muted-foreground rounded-xl border px-4 py-3 text-sm">
        {item.text ?? "Action review is unavailable."}
      </div>
    );
  }
  const status = guardianStatuses.find(
    (candidate) => candidate === item.status,
  );
  const review: ChatApprovalCardReview = {
    id: item.guardianReviewId,
    runId: item.runId,
    status: status ?? "pending_guardian",
    approvalQuestion: item.text,
    action: item.payload,
    actionRequiresDetail: true,
  };

  return (
    <div>
      <ChatApprovalCard
        review={review}
        detail={props.approvalDetail}
        isApprovalLoading={props.isApprovalLoading}
        isDecidingApproval={props.isDecidingApproval}
        onLoadApprovalDetail={props.onLoadApprovalDetail}
        onDecideApproval={props.onDecideApproval}
      />
    </div>
  );
}

type DisplayFileReference = {
  fileContentId: string;
  processingId?: string;
  fileName: string;
  pageCount?: number | null;
};

function displayFiles(value: unknown): DisplayFileReference[] {
  if (!Array.isArray(value) || value.length > 50) return [];
  return value.filter(
    (file): file is DisplayFileReference =>
      typeof file === "object" &&
      file !== null &&
      typeof file.fileContentId === "string" &&
      typeof file.fileName === "string" &&
      (file.processingId == null || typeof file.processingId === "string") &&
      (file.pageCount == null || typeof file.pageCount === "number"),
  );
}

function UserItem({
  item,
  body,
  isBodyLoading,
  onLoadItemBody,
}: ChatTimelineItemProps) {
  const container = useRef<HTMLDivElement>(null);
  const attemptedChunk = useRef<string>();
  const [nearViewport, setNearViewport] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const compactFiles = displayFiles(item.payload.fileReferences);
  const needsData = item.hasBody && !Array.isArray(item.payload.fileReferences);
  const dataBody =
    body?.part === "data" && body.revision === item.revision ? body : undefined;
  let attachments = compactFiles;
  let invalidData = false;
  if (needsData && dataBody?.isComplete) {
    try {
      const data = JSON.parse(dataBody.text);
      const references = data?.display?.fileReferences;
      attachments = displayFiles(references);
      invalidData =
        references !== undefined &&
        (!Array.isArray(references) ||
          references.length !== attachments.length);
    } catch {
      invalidData = true;
    }
  }
  const loadAttachments = useCallback(async () => {
    if (!onLoadItemBody) return;
    setLoadError(false);
    try {
      await onLoadItemBody(item, "data");
    } catch {
      setLoadError(true);
    }
  }, [item, onLoadItemBody]);
  useEffect(() => {
    if (!needsData || !container.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setNearViewport(entry.isIntersecting);
        if (!entry.isIntersecting) attemptedChunk.current = undefined;
      },
      { rootMargin: "200px" },
    );
    observer.observe(container.current);
    return () => {
      observer.disconnect();
    };
  }, [needsData]);
  useEffect(() => {
    if (
      !nearViewport ||
      !needsData ||
      !onLoadItemBody ||
      isBodyLoading ||
      loadError ||
      dataBody?.isComplete ||
      dataBody?.reachedClientLimit
    )
      return;
    const chunk = `${item.id}:${item.revision}:${dataBody?.text.length ?? 0}`;
    if (attemptedChunk.current === chunk) return;
    attemptedChunk.current = chunk;
    void loadAttachments();
  }, [
    nearViewport,
    needsData,
    onLoadItemBody,
    isBodyLoading,
    loadError,
    dataBody,
    item.id,
    item.revision,
    loadAttachments,
  ]);
  const failed = loadError || invalidData || dataBody?.reachedClientLimit;
  return (
    <div ref={container} className="ml-auto max-w-[85%] sm:max-w-[75%]">
      {attachments.length > 0 && (
        <div
          aria-label="Attached files"
          className="mb-2 flex flex-wrap justify-end gap-2"
        >
          {attachments.map((file) => (
            <ChatAttachmentPreview
              key={file.fileContentId}
              file={{
                id: file.fileContentId,
                fileName: file.fileName,
                processingId: file.processingId ?? undefined,
                pageCount: file.pageCount,
              }}
            />
          ))}
        </div>
      )}
      {needsData && onLoadItemBody && !dataBody?.isComplete && !failed && (
        <p
          role="status"
          className="text-muted-foreground mb-2 text-right text-xs"
        >
          Loading attachments…
        </p>
      )}
      {failed && (
        <div className="mb-2 text-right text-xs">
          <p role="alert">The attachments could not be loaded.</p>
          {loadError && onLoadItemBody && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={isBodyLoading}
              onClick={() => void loadAttachments()}
            >
              Retry
            </Button>
          )}
        </div>
      )}
      <div
        data-chat-search-content
        className="bg-foreground text-background ml-auto w-fit max-w-full rounded-3xl px-5 py-3 text-sm leading-6 [overflow-wrap:anywhere] whitespace-pre-wrap"
      >
        {item.text}
      </div>
    </div>
  );
}

export const ChatTimelineItem = memo(function ChatTimelineItem(
  props: ChatTimelineItemProps,
) {
  const { item } = props;
  let content;
  if (item.kind === "assistant_message" || item.kind === "commentary") {
    content = <AssistantItem {...props} />;
  } else if (isUserQuestionItem(item)) {
    content = <ChatUserQuestion {...props} />;
  } else if (item.kind === "tool_activity") {
    content = <ChatToolItem {...props} />;
  } else if (item.kind === "approval_request") {
    content = <ApprovalItem {...props} />;
  } else {
    content = <UserItem {...props} />;
  }

  return (
    <div
      data-chat-item-id={item.id}
      className="mx-auto w-full max-w-[60rem] px-5 py-3 sm:px-8"
    >
      {content}
      {props.footer}
    </div>
  );
});
